import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../../../../config/firebaseConfig";
import { fmtDec } from "../../utils";

/**
 * Custom hook to fetch citernes data for all stations
 * Returns a map of stationId -> citernes data
 */
export function useCiternes(produit) {
  const [citernesMap, setCiternesMap] = useState({});

  useEffect(() => {
    const usersRef = collection(db, "users");
    const unsub = onSnapshot(usersRef, (snap) => {
      const map = {};

      snap.forEach((doc) => {
        const userData = doc.data();
        if (userData.station) {
          const stationId = userData.station.toLowerCase();

          // Parse citerne definitions (C1, C2, C3, C4)
          const citerneDefs = {};
          Object.keys(userData).forEach((key) => {
            if (key.startsWith("C") && userData[key]) {
              const parts = userData[key].split("_");
              if (parts.length >= 4) {
                const type = parts[0]; // SU or GO
                const maxCm = parseFloat(parts[1]) || 0; // Max height in cm
                const capacity = parseFloat(parts[2]) || 0; // Capacity in m³
                const k = parseFloat(parts[3]) || 0; // K coefficient

                const isEssence = type === "SU";
                const isGO = type === "GO";

                if (
                  (produit === "essence" && isEssence) ||
                  (produit === "go" && isGO)
                ) {
                  citerneDefs[key] = {
                    type: type,
                    maxCm: maxCm,
                    capacity: capacity,
                    k: k,
                  };
                }
              }
            }
          });

          // Fetch all data documents and find latest readings
          if (Object.keys(citerneDefs).length > 0) {
            const dataRef = collection(db, "users", doc.id, "data");
            onSnapshot(dataRef, (dataSnap) => {
              const allDocs = dataSnap.docs;
              if (allDocs.length === 0) return;

              // Sort documents by date (newest first)
              const sortedDocs = allDocs.sort((a, b) => {
                const dateA = a.id.split("-").reverse().join("-");
                const dateB = b.id.split("-").reverse().join("-");
                return dateB.localeCompare(dateA);
              });

              // Find the latest reading for each citerne
              const latestReadings = {};

              // Get today's date in DD-MM-YYYY format
              const today = new Date();
              const todayStr = `${String(today.getDate()).padStart(2, "0")}-${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`;

              // First pass: Look for today's morning CUVES readings
              const todayDoc = sortedDocs.find((d) => d.id === todayStr);
              if (todayDoc) {
                const todayData = todayDoc.data();
                Object.entries(todayData).forEach(([key, value]) => {
                  if (key.startsWith("C") && value && value.type === "CUVES") {
                    const citerneId = key;
                    const cm =
                      parseFloat(String(value.value).replace(",", ".")) || 0;
                    latestReadings[citerneId] = { cm, source: "CUVES_TODAY" };
                  }
                });
              }

              // Second pass: If some citernes missing, look for CUVES from previous days (not APPROV)
              const foundCount = Object.keys(latestReadings).length;
              const neededCount = Object.keys(citerneDefs).length;

              if (foundCount < neededCount) {
                // Iterate through all documents from newest to oldest (skip today)
                for (const dataDoc of sortedDocs) {
                  if (dataDoc.id === todayStr) continue; // Skip today, already checked

                  const data = dataDoc.data();

                  Object.entries(data).forEach(([key, value]) => {
                    // Check for CUVES readings from previous days only
                    if (
                      key.startsWith("C") &&
                      value &&
                      value.type === "CUVES"
                    ) {
                      const citerneId = key;
                      if (!latestReadings[citerneId]) {
                        const cm =
                          parseFloat(String(value.value).replace(",", ".")) ||
                          0;
                        latestReadings[citerneId] = {
                          cm,
                          source: "CUVES_PREVIOUS",
                          date: dataDoc.id,
                        };
                      }
                    }
                  });

                  // Stop if we have readings for all citernes
                  const currentFound = Object.keys(latestReadings).length;
                  if (currentFound >= neededCount) break;
                }
              }

              // Build citernes object with latest readings
              const citernes = {};
              let totalVolume = 0;
              Object.entries(citerneDefs).forEach(([citerneId, def]) => {
                const reading = latestReadings[citerneId];
                if (reading) {
                  const volume = reading.cm * def.k; // cm × K = m³
                  totalVolume += volume;

                  console.log(
                    `[Citerne Detail] ${stationId}/${citerneId}:`,
                    `cm=${reading.cm}, K=${def.k}, volume=${fmtDec(volume, 3)} m³`,
                    `source=${reading.source}`,
                  );

                  citernes[citerneId] = {
                    label: citerneId,
                    cm: reading.cm,
                    k: def.k,
                    volume: volume,
                    capacite: def.capacity,
                  };
                } else {
                  console.warn(
                    `[Citerne Warning] ${stationId}/${citerneId}: No reading found!`,
                  );
                }
              });

              console.log(
                `[CiternesMap] Station ${stationId}:`,
                `Total Volume: ${fmtDec(totalVolume, 3)} m³`,
                `Total Capacity: ${fmtDec(
                  Object.values(citerneDefs).reduce(
                    (sum, d) => sum + d.capacity,
                    0,
                  ),
                  3,
                )} m³`,
                `Citernes count: ${Object.keys(citernes).length}/${Object.keys(citerneDefs).length}`,
              );

              if (Object.keys(citernes).length > 0) {
                map[stationId] = citernes;
                setCiternesMap({ ...map });
              } else {
                console.warn(
                  `[CiternesMap] Station ${stationId}: No citernes with readings found!`,
                );
              }
            });
          }
        }
      });
    });

    return () => unsub();
  }, [produit]);

  return citernesMap;
}
